import type { FC, ReactNode } from 'react'

import Link from 'next/link'
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { CircleAlert, CircleCheck, Info, X } from 'lucide-react'
import { cn } from '@/lib/utils'

type ToastTone = 'success' | 'error' | 'info'

interface ToastAction {
    label: string
    href?: string
    onClick?: () => void
}

interface ToastItem {
    id: number
    message: string
    tone: ToastTone
    action?: ToastAction
}

interface ToastContextValue {
    notify: (message: string, tone?: ToastTone, action?: ToastAction) => void
}

const ToastContext = createContext<ToastContextValue>({ notify: () => undefined })

export const useToast = (): ToastContextValue => useContext(ToastContext)

const icons: Record<ToastTone, ReactNode> = {
    success: <CircleCheck className='size-5 text-emerald-400' aria-hidden='true' />,
    error: <CircleAlert className='size-5 text-red-400' aria-hidden='true' />,
    info: <Info className='size-5 text-cyan-300' aria-hidden='true' />
}

export const ToastProvider: FC<{ children: ReactNode }> = ({ children }) => {
    const [toasts, setToasts] = useState<Array<ToastItem>>([])
    const counter = useRef(0)
    const timers = useRef(new Map<number, number>())

    const dismiss = useCallback((id: number) => {
        setToasts(current => current.filter(toast => toast.id !== id))

        const timer = timers.current.get(id)

        if (timer) window.clearTimeout(timer)

        timers.current.delete(id)
    }, [])

    const notify = useCallback((message: string, tone: ToastTone = 'success', action?: ToastAction) => {
        counter.current += 1

        const id = counter.current

        setToasts(current => [...current.slice(-2), { id, message, tone, action }])
        timers.current.set(id, window.setTimeout(() => dismiss(id), tone === 'error' ? 6000 : 3800))
    }, [dismiss])

    useEffect(() => {
        const map = timers.current

        return () => {
            map.forEach(timer => window.clearTimeout(timer))
        }
    }, [])

    const value = useMemo(() => ({ notify }), [notify])

    return (
        <ToastContext.Provider value={value}>
            {children}

            <div className='pointer-events-none fixed inset-x-0 bottom-[max(1rem,env(safe-area-inset-bottom))] z-[90] flex flex-col items-center gap-2 px-4' aria-live='polite' role='status'>
                {toasts.map(toast => (
                    <div
                        key={toast.id}
                        className={cn('pointer-events-auto flex w-full max-w-md items-center gap-3 rounded-2xl bg-navy-950/95 px-4 py-3 text-sm text-white shadow-lift backdrop-blur animate-in fade-in slide-in-from-bottom-4 duration-300')}
                    >
                        {icons[toast.tone]}

                        <p className='flex-1 leading-relaxed'>{toast.message}</p>

                        {toast.action && (toast.action.href
                            ? <Link href={toast.action.href} onClick={() => dismiss(toast.id)} className='shrink-0 rounded-full bg-white/10 px-3 py-1.5 text-xs font-semibold text-cyan-200 transition-colors hover:bg-white/20'>{toast.action.label}</Link>
                            : <button type='button' onClick={() => { toast.action?.onClick?.(); dismiss(toast.id) }} className='shrink-0 rounded-full bg-white/10 px-3 py-1.5 text-xs font-semibold text-cyan-200 transition-colors hover:bg-white/20'>{toast.action.label}</button>)}

                        <button type='button' onClick={() => dismiss(toast.id)} className='shrink-0 rounded-full p-1 text-white/60 transition-colors hover:text-white' aria-label='Dismiss'>
                            <X className='size-4' />
                        </button>
                    </div>
                ))}
            </div>
        </ToastContext.Provider>
    )
}
